import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { combineLatest, Observable } from 'rxjs';
import { catchError, map, switchMap, filter, timeout } from 'rxjs/operators';
import { AuthFacade } from '@cigna/chcp/auth/data-access';

import {
  checkValidResponse,
  logError,
  ServiceError,
  SupportingInformation,
  ReconsiderationType,
  apiGatewayTimeout,
} from '@cigna/chcp/shared/util';

import {
  Adjustment,
  MemberGroupRequest,
  ClaimsAdjustmentsResponse,
} from '@cigna/chcp/claims/shared/util';
import { LOB_DENTAL } from '@cigna/chcp/auth/util';

@Injectable({
  providedIn: 'root',
})
export class ReconsiderationService {
  public patientLocatorApi: string;
  public CONSUMER_CODE = '1000';
  private reconsiderationUrl$: Observable<string>;
  private medical = 'MED';

  constructor(
    private http: HttpClient,
    private authFacade: AuthFacade,
    @Inject('secureApi') secureApi: string,
  ) {
    this.reconsiderationUrl$ = this.authFacade.lob$.pipe(
      filter((lob) => !!lob),
      map(
        (lob) =>
          `${secureApi}/reconsideration/${
            lob === LOB_DENTAL ? 'dental' : 'medical'
          }/v1`,
      ),
    );
    this.patientLocatorApi = `${secureApi}/patient/locator/v1/individuals`;
  }

  private getAdjustmentForRequest(adjustment: Adjustment): Partial<Adjustment> {
    return { ...adjustment, attachmentsData: undefined };
  }

  removeAdjustment(
    id: string,
    reconType: ReconsiderationType,
    isPrecert = false,
    coverageType = '',
  ): Observable<ServiceError | Adjustment[]> {
    const params: HttpParams = new HttpParams().set(
      'reconsiderationType',
      reconType,
    );
    return this.reconsiderationUrl$.pipe(
      switchMap((reconUrl) =>
        this.http
          .delete(
            `${reconUrl}${
              isPrecert && coverageType === this.medical ? '/precert' : ''
            }/${id}`,
            {
              params,
            },
          )
          .pipe(
            map(checkValidResponse),
            map((res: ClaimsAdjustmentsResponse) => res.adjustmentsHistory),
            catchError(logError),
          ),
      ),
    );
  }

  fetchReconHistory(
    key: 'claimref' | 'precertid',
    value: string,
  ): Observable<ServiceError | Adjustment[]> {
    const params: HttpParams = new HttpParams()
      .set('asof', new Date().getTime().toString())
      .set(key, value);

    return this.reconsiderationUrl$.pipe(
      switchMap((reconUrl) =>
        this.http
          .get(`${reconUrl}${key === 'precertid' ? '/precert' : ''}/history`, {
            params,
          })
          .pipe(
            map(checkValidResponse),
            map((res: ClaimsAdjustmentsResponse) => res.adjustmentsHistory),
            catchError(logError),
          ),
      ),
    );
  }

  getQuestionnaire() {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );
    return combineLatest([this.authFacade.lob$, this.reconsiderationUrl$]).pipe(
      switchMap(([lob, reconUrl]) =>
        this.http.get(`${reconUrl}/questionnaire`, { params }).pipe(
          map(checkValidResponse),
          map((res) => ({
            questionnaire:
              lob === LOB_DENTAL
                ? res.questionnaire
                : res.questionnaire.filter((q: any) => q.type === 'timely'),
            questionnaireNonTimely:
              lob === LOB_DENTAL
                ? []
                : res.questionnaire.filter((q: any) => q.type === 'non-timely'),
          })),
          catchError(logError),
        ),
      ),
    );
  }

  saveAdjustment(data: Adjustment, isPrecert = false) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );
    return this.reconsiderationUrl$.pipe(
      switchMap((reconUrl) =>
        this.http
          .post(
            `${reconUrl}${
              isPrecert && data.coverageType === this.medical ? '/precert' : ''
            }`,
            this.getAdjustmentForRequest(data),
            { params },
          )
          .pipe(
            map(checkValidResponse),
            map((res) => res.adjustment),
            catchError(logError),
          ),
      ),
    );
  }

  updateAdjustment(data: Adjustment, isPrecert = false) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );
    return this.reconsiderationUrl$.pipe(
      switchMap((reconUrl) =>
        this.http
          .put(
            `${reconUrl}${
              isPrecert && data.coverageType === this.medical ? '/precert' : ''
            }/${data.id}`,
            this.getAdjustmentForRequest(data),
            {
              params,
            },
          )
          .pipe(
            map(checkValidResponse),
            map((res) => res.adjustment),
            catchError(logError),
          ),
      ),
    );
  }

  submitAdjustment(data: Adjustment, isPrecert = false) {
    const params: HttpParams = new HttpParams()
      .set('consumerCode', this.CONSUMER_CODE)
      .set('claimReferenceNumber', data.claimReferenceNumber || '');
    return this.reconsiderationUrl$.pipe(
      switchMap((reconUrl) =>
        this.http
          .post(
            `${reconUrl}${
              isPrecert && data.coverageType === this.medical ? '/precert' : ''
            }/submit`,
            this.getAdjustmentForRequest(data),
            {
              params,
            },
          )
          .pipe(
            map(checkValidResponse),
            map((res) => res.adjustment),
            catchError(logError),
            timeout(apiGatewayTimeout),
          ),
      ),
    );
  }

  getMemberGroupInfo(data: MemberGroupRequest) {
    const params: HttpParams = new HttpParams()
      .set('memberFirstName', data.memberFirstName)
      .set('memberLastName', data.memberLastName)
      .set('memberDateOfBirth', data.memberDateOfBirth)
      .set('memberId', data.memberId)
      .set('relationshipCode', data.relationshipCode)
      .set('coverageType', data.coverageType)
      .set('memberIdentifier', data.memberIdentifier)
      .set('eligibilityAsOfDate', data.eligibilityAsOfDate)
      .set('consumerCode', this.CONSUMER_CODE);
    return this.http
      .get(`${this.patientLocatorApi}/unique/member`, { params })
      .pipe(
        map(checkValidResponse),
        map((res) => res.memberSummary),
        catchError(logError),
      );
  }

  submitClaimSupportingInformation(
    req: SupportingInformation,
  ): Observable<SupportingInformation> {
    const params: HttpParams = new HttpParams().set(
      'claimRef',
      req.claimReferenceNumber,
    );
    return this.reconsiderationUrl$.pipe(
      switchMap((reconUrl) =>
        this.http.post(`${reconUrl}/pended/submit`, req, { params }).pipe(
          map(checkValidResponse),
          map((res) => res.supportingInfoSubmission),
          catchError(logError),
        ),
      ),
    );
  }

  getClaimSupportingInformation(
    claimReferenceNumber: string,
    sourceSystemCode: string,
    claimEngineClaimID: string,
  ): Observable<SupportingInformation[]> {
    const params: HttpParams = new HttpParams()
      .set('asof', new Date().getTime().toString())
      .set('claimReferenceNumber', claimReferenceNumber)
      .set('sourceSystemCode', sourceSystemCode)
      .set('claimEngineClaimID', claimEngineClaimID);

    return this.reconsiderationUrl$.pipe(
      switchMap((reconUrl) =>
        this.http.get(`${reconUrl}/pended/claim-info`, { params }).pipe(
          map(checkValidResponse),
          map((res) => res.claimInfo),
          catchError(logError),
        ),
      ),
    );
  }
}
